import { parseISO, parseJSON } from "date-fns";
import nb from "date-fns/locale/nb";
import toDate from "date-fns/toDate";
import isNumber from "is-number";
import {
  action,
  flow,
  IReactionDisposer,
  isObservableArray,
  observable,
  when,
} from "mobx";
import * as React from "react";

export function isIE() {
  const ua = window.navigator.userAgent; //Check the userAgent property of the window.navigator object
  const msie = ua.indexOf("MSIE "); // IE 10 or older
  const trident = ua.indexOf("Trident/"); //IE 11

  return msie > 0 || trident > 0;
}

let idCount = 0;
export const idCounter = () => ++idCount;

export const removeScriptsFromText = (text) => {
  return text.replace(/<script([\S\s]*?)>([\S\s]*?)<\/script>/gi, "");
};

const getLast = (arr: any[], n: number) => {
  return arr.slice(Math.max(arr.length - n, 0));
};

export function flattenChildren(children, depth = 0) {
  const arr = getLast(React.Children.toArray(children), 10);

  return arr.reduce((flatChildren, child) => {
    if (child.type === React.Fragment) {
      return flatChildren.concat(flattenChildren(child.props.children));
    }
    flatChildren.push(child);
    if (depth < 2 && child && child.props && child.props.children) {
      flatChildren.push(...flattenChildren(child.props.children), depth + 1);
    }
    return flatChildren;
  }, []);
}

export function decodeHtml(html) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}
(window as any).decodeHtml = decodeHtml;

const tryDecodeHtml = (r) => {
  try {
    return decodeHtml(r);
  } catch (e) {
    return r;
  }
};

export const decodeHtmlRecursively = (r) => {
  if (r === null) return null;
  if (r === undefined) return undefined;

  if (Array.isArray(r)) {
    return r.map(decodeHtmlRecursively);
  }
  if (typeof r === "object") {
    return Object.entries(r).reduce((acc, keyval) => {
      const key = tryDecodeHtml(keyval[0]);
      acc[key] = decodeHtmlRecursively(keyval[1]);

      return acc;
    }, {});
  }

  return tryDecodeHtml(r);
};

export const getCgiBaseUrl = () => {
  let development = false;
  try {
    development = process.env.NODE_ENV === "development";
  } catch (e) {}

  return development
    ? "https://secure.webtemp.no/cgi-win/test/jorgen"
    : "https://secure.webtemp.no/cgi-win";
};

export function isScrolledIntoView(el) {
  const rect = el.getBoundingClientRect();
  const elemTop = rect.top;
  const elemBottom = rect.bottom;

  // Only completely visible elements return true:
  let isVisible = elemTop >= 0 && elemBottom <= window.innerHeight;
  // Partially visible elements return true:
  isVisible = isVisible || (elemTop < window.innerHeight && elemBottom >= 0);
  return isVisible;
}

export function makeid() {
  let text = "";
  const possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  for (let i = 0; i < 7; i++)
    text += possible.charAt(Math.floor(Math.random() * possible.length));

  return text;
}

export function guid(): string {
  return uuidv4(undefined).replace(/-/g, "");
}

//  @ts-ignore
// prettier-ignore
function uuidv4(a){return a?(a^Math.random()*16>>a/4).toString(16):([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,uuidv4)}

export const trySafe = (func) => {
  try {
    return func();
  } catch (e) {}
};

export const concatClassnames: (classNames: Array<any>) => string = (
  classNames
) =>
  classNames
    .reduce((acc, className) => {
      if (isArrayOrObservableArray(className)) {
        className = concatClassnames(className);
      }

      return acc + " " + (className ? className : "");
    }, "")
    .trim();

export const DOMCollectionToArray = (domCollection) => {
  let array = [];
  for (let i = 0; i < domCollection.length; i++) {
    array.push(domCollection[i]);
  }
  return array;
};

export const wait = (millisecondsToWait) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(undefined);
    }, millisecondsToWait);
  });
};

export const capitalizeWords = (words) =>
  words.replace(/(^|\s)\S/g, (l) => l.toUpperCase());

export const capitalizeFirstWord = (words) => {
  return words
    .split(" ")
    .map((word, index) => {
      if (index === 0) {
        return word.replace(/(^|\s)\S/g, (l) => l.toUpperCase());
      }

      return word;
    })
    .join(" ");
};

export const isArrayOrObservableArray = (x) => {
  return Array.isArray(x) || isObservableArray(x);
};

export const arraysEqual = (a, b) => {
  if (!a || !isArrayOrObservableArray(a)) return false;
  if (!b || !isArrayOrObservableArray(b)) return false;

  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }

  return true;
};

export const firstDefined = (values: any[]) =>
  values.find((v) => v !== undefined);

export const valueOrDefault = (value, defaultValue) => {
  if (value !== undefined) return value;
  return defaultValue;
};

export function tryOrDefault<T>(functionToTry: () => T, defaultValue: T): T;
export function tryOrDefault<T>(
  functionToTry: () => T | Promise<T>,
  defaultValue: T
): T;
export function tryOrDefault<T>(
  functionToTry: () => T | Promise<T>,
  defaultValue: T
) {
  try {
    return functionToTry();
  } catch (e) {
    return defaultValue;
  }
}

function hashFnv32a(str, asString, seed) {
  /*jshint bitwise:false */
  let i,
    l,
    hval = seed === undefined ? 0x811c9dc5 : seed;

  for (i = 0, l = str.length; i < l; i++) {
    hval ^= str.charCodeAt(i);
    hval +=
      (hval << 1) + (hval << 4) + (hval << 7) + (hval << 8) + (hval << 24);
  }
  if (asString) {
    // Convert to 8 digit hex string
    return ("0000000" + (hval >>> 0).toString(16)).substr(-8);
  }
  return hval >>> 0;
}

export function hashCode(string): string {
  return hashFnv32a(string, true, "12345").toString();
}

export function modulo(n, m) {
  return ((n % m) + m) % m;
}

export const convertAllKeysToLowerCase = (obj) => {
  return Object.keys(obj).reduce((acc, key) => {
    acc[key.toLowerCase()] = obj[key];
    return acc;
  }, {});
};

export const removedKeysWereValueIsUndefined = (obj) => {
  return Object.keys(obj).reduce((acc, key) => {
    if (obj[key] !== undefined) acc[key] = obj[key];
    return acc;
  }, {});
};

export const plural = (n, textIf1, otherwise) => {
  if (n === 1) return textIf1;
  return otherwise;
};

export const isBurgerTime = () => {
  return new Date().getDay() === 3;
};

export function isEvtWithFiles(event) {
  if (!event.dataTransfer) {
    return !!event.target && !!event.target.files;
  }
  // https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer/types
  // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Recommended_drag_types#file
  return Array.prototype.some.call(
    event.dataTransfer.types,
    (type) => type === "Files" || type === "application/x-moz-file"
  );
}

export const waitUntil = (predicate) => {
  return new Promise((resolve) => {
    when(predicate, () => resolve(undefined));
  });
};

let _isCssStickySupported;
export const isCssStickySupported = () => {
  if (_isCssStickySupported !== undefined) return _isCssStickySupported;

  const el = document.createElement("a");
  el.style.position = "sticky";

  _isCssStickySupported = el.style.position === "sticky";
  return _isCssStickySupported;
};

export const removeUndefined = (obj: Object) =>
  Object.keys(obj).reduce((acc, key) => {
    const val = obj[key];
    if (val !== undefined) {
      acc[key] = val;
    }
    return acc;
  }, {});

export const removeNull = (obj: Object) =>
  Object.keys(obj).reduce((acc, key) => {
    const val = obj[key];
    if (val !== null) {
      acc[key] = val;
    }
    return acc;
  }, {});

export const removeRecursive = (obj: Object, remove: (val) => boolean) => {
  const result = Object.keys(obj).reduce((acc, key) => {
    let val = obj[key];

    if (typeof val === "object" && val) {
      val = removeRecursive(val, remove);
    }

    if (!remove(val)) {
      acc[key] = val;
    }
    return acc;
  }, {});

  if (Object.keys(result).length === 0) return null;

  return result;
};

export const hexToRgba = (hex: string, a: number) => {
  let r = parseInt(hex.substring(1, 3), 16);
  let g = parseInt(hex.substring(3, 5), 16);
  let b = parseInt(hex.substring(5), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const onKeyDown = (key: string, func: Function) => {
  return {
    onKeyDown: (evt) => {
      if (evt.key === key) func();
    },
  };
};

/** Adapted from C# FieldFormatter.FormatPhoneNumberDisplay */
export const formatPhone = (phone: string) => {
  let cleaned = phone.replace(/\s/g, "").replace(/^\+/, "00");
  const canFormat =
    isNumber(cleaned) && cleaned.length >= 8 && cleaned.length <= 13;
  if (!canFormat) return phone.trim();

  let lasteight = cleaned.substr(cleaned.length - 8);
  let front = cleaned.substr(0, cleaned.length - 8);

  if (front.startsWith("00") && front.length > 2) {
    front = `+ ${front.substr(2)}`;
  }

  const isMobile = lasteight.startsWith("4") || lasteight.startsWith("9");

  if (isMobile) {
    const a = lasteight.slice(0, 3);
    const b = lasteight.slice(3, 5);
    const c = lasteight.slice(5, 8);

    return `${front} ${a} ${b} ${c}`.trim();
  }

  const a = lasteight.slice(0, 2);
  const b = lasteight.slice(2, 4);
  const c = lasteight.slice(4, 6);
  const d = lasteight.slice(6, 8);

  return `${front} ${a} ${b} ${c} ${d}`.trim();
};

const defaultFormatter = new Intl.NumberFormat("no-NO", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const formatNumber = (
  num: string | number,
  formatter = defaultFormatter
) => {
  if (num === null || num === undefined) return "";
  if (typeof num === "string") num = Number(num);

  return formatter.format(num);
};

export const awaitableWhen = (
  f: () => any,
  onWaiting?: (disposer: IReactionDisposer) => any
) =>
  new Promise((r) => {
    const disposer = when(f, () => r(null));
    if (onWaiting) onWaiting(disposer);
    return disposer;
  });

/**
 * Stopping propagation seems to only work in
 * development mode, and not in production mode.
 *
 * Calling evt.persist seems to fix it in production.
 */
export const persistAndStopPropagation = (evt: any) => {
  if (typeof evt.persist === "function") evt.persist();
  if (typeof evt.stopPropagation === "function") evt.stopPropagation();
};

/** Helper when you want to debounce and also wait until async function is done */
export const getSequentialDebouncer = () => {
  let _id = 0;
  const _idCounter = () => ++_id;

  let lastCallId = -1;
  let waiting = observable.box(false);

  return {
    start: flow(function* () {
      const myCallId = _idCounter();
      lastCallId = myCallId;

      const imLast = () => myCallId === lastCallId;

      yield wait(500);
      yield awaitableWhen(() => !waiting.get());
      if (!imLast()) return false;

      waiting.set(true);
      return true;
    }),
    stop: action(() => {
      waiting.set(false);
    }),
  };
};

export const defDFnsOpt = {
  weekStartsOn: 1 as 0 | 1 | 4 | 2 | 3 | 5 | 6,
  locale: nb,
};

export function isIsoDate(str: string) {
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;
  const d = new Date(str);
  return d.toISOString() === str;
}

export function isJsonDate(str: string) {
  if (typeof str !== "string") return false;
  return !!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(str);
}

export const parseDate = (datestring: string) => {
  if (!datestring) return null;
  if (typeof datestring !== "string") return null;

  if (isJsonDate(datestring)) {
    return parseJSON(datestring);
  }

  if (isIsoDate(datestring)) {
    return parseISO(datestring);
  }

  const digits = Number(datestring.match(/\d+/g)[0]);

  if (digits) {
    return toDate(digits);
  }

  return null;
};

/** Convert PascalCase to camelCase */
export function toCamelCase(o) {
  let newO, origKey, newKey, value;
  if (o instanceof Array) {
    return o.map(function (value) {
      if (typeof value === "object") {
        value = toCamelCase(value);
      }
      return value;
    });
  } else {
    newO = {};
    for (origKey in o) {
      if (o.hasOwnProperty(origKey)) {
        newKey = (
          origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey
        ).toString();
        value = o[origKey];
        if (
          value instanceof Array ||
          (value !== null && value.constructor === Object)
        ) {
          value = toCamelCase(value);
        }
        newO[newKey] = value;
      }
    }
  }
  return newO;
}

export const splitUnit = (v: string) => {
  if (typeof v === "string" && v !== "") {
    const split = v.match(/^([-.\d]+(?:\.\d+)?)(.*)$/);
    return [split[1].trim(), split[2].trim()];
  } else {
    return [v, ""];
  }
};

let _isTouchCapable;
export const isTouchCapable = () => {
  if (_isTouchCapable !== undefined) {
    return _isTouchCapable;
  }
  try {
    document.createEvent("TouchEvent");
    _isTouchCapable = true;
    return true;
  } catch (e) {
    _isTouchCapable = false;
    return false;
  }
};

export const findSelfOrParent = (node, f) => {
  if (!node) return null;
  try {
    if (f(node)) return node;
  } catch (e) {}

  if (!node.parentNode) return null;
  return findSelfOrParent(node.parentNode, f);
};

export function filterKeys<T>(
  obj: { [key: string]: T },
  filter: (key: string) => boolean
) {
  return Object.entries(obj).reduce((acc, [key, val]) => {
    if (filter(key)) acc[key] = val;
    return acc;
  }, {});
}
